import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Wordmark } from "./Wordmark";
import api from "@/lib/api";

export function Header() {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null); // 'cap' | 'ind'
  const [nav, setNav] = useState({ practices: [], industries: [], countries: [] });
  const location = useLocation();

  useEffect(() => { api.get("/nav").then(({ data }) => setNav(data)).catch(() => {}); }, []);
  useEffect(() => { setOpen(false); setMenu(null); }, [location.pathname]);

  const link = "text-sm text-charcoal hover:text-plum transition-colors";
  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E5E5E5]">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="header-logo-link" aria-label="ThinkStack home"><Wordmark /></Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            <div className="relative" onMouseEnter={() => setMenu("cap")} onMouseLeave={() => setMenu(null)}>
              <button data-testid="nav-capabilities" className={`flex items-center gap-1 ${link}`}>Capabilities <ChevronDown size={14} /></button>
              {menu === "cap" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[720px]">
                  <div className="border border-[#E5E5E5] bg-white shadow-lg p-6 grid grid-cols-2 gap-6">
                    {nav.practices.map((p) => (
                      <div key={p.slug}>
                        <Link to={`/capabilities/${p.slug}`} data-testid={`mega-${p.slug}`} className="font-serif text-lg text-deep-navy hover:text-plum" style={{ borderLeft: `3px solid ${p.accent}`, paddingLeft: 10 }}>{p.name}</Link>
                        <ul className="mt-2 pl-3 space-y-1">
                          {p.solutions.slice(0, 4).map((s) => (<li key={s.to}><Link to={s.to} className="text-xs text-charcoal/60 hover:text-plum">{s.name}</Link></li>))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" onMouseEnter={() => setMenu("ind")} onMouseLeave={() => setMenu(null)}>
              <button data-testid="nav-industries" className={`flex items-center gap-1 ${link}`}>Industries <ChevronDown size={14} /></button>
              {menu === "ind" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64">
                  <div className="border border-[#E5E5E5] bg-white shadow-lg py-2 max-h-[60vh] overflow-y-auto">
                    {nav.industries.map((it) => (<Link key={it.slug} to={`/industries/${it.slug}`} className="block px-5 py-2 text-sm text-charcoal hover:bg-off-white hover:text-plum">{it.name}</Link>))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/capabilities/enterprise-ai-automation" className={link}>AI Transformation</Link>
            <Link to="/insights" data-testid="nav-insights" className={link}>Insights</Link>
            <Link to="/about" data-testid="nav-about" className={link}>About</Link>
            <Link to="/contact" data-testid="nav-cta" className="text-sm bg-deep-navy text-white px-5 py-2.5 hover:bg-plum transition-colors" style={{ "--tw-bg-opacity": 1 }}>Discuss a Transformation</Link>
          </nav>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-charcoal" aria-label="Toggle menu" onClick={() => setOpen((v) => !v)}>{open ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E5E5] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-6 max-h-[80vh] overflow-y-auto">
            <div><p className="overline text-charcoal/50 mb-3">Capabilities</p>{nav.practices.map((p) => (<Link key={p.slug} to={`/capabilities/${p.slug}`} className="block py-2 text-charcoal">{p.name}</Link>))}</div>
            <div><p className="overline text-charcoal/50 mb-3">Industries</p>{nav.industries.slice(0, 6).map((it) => (<Link key={it.slug} to={`/industries/${it.slug}`} className="block py-2 text-charcoal">{it.name}</Link>))}<Link to="/industries" className="block py-2 text-plum font-medium">All industries →</Link></div>
            <div><p className="overline text-charcoal/50 mb-3">Company</p><Link to="/use-cases" className="block py-2 text-charcoal">Use Cases</Link><Link to="/insights" className="block py-2 text-charcoal">Insights</Link><Link to="/about" className="block py-2 text-charcoal">About</Link></div>
            <Link to="/contact" data-testid="mobile-nav-cta" className="block text-center bg-deep-navy text-white px-5 py-3">Discuss a Transformation</Link>
          </div>
        </div>
      )}
    </header>
  );
}
